import customerService from '../../services/customerService';
import jobService from '../../services/jobService';
import appointmentService from '../../services/appointmentService';
import { getFranchiseeIdForCRUD } from '../services/appHelpers';
import { JOB_STATUS, VIEWS } from '../utils/appConstants';
import { useToast } from '../../components/Toast';

export const useAppCRUD = (user, appData, navigation, modalActions) => {
  const { refreshData } = appData;
  const { setSelectedCustomer, setSelectedJob, setCurrentView } = navigation;
  const { closeAddCustomerModal, closeAddJobModal, closeAddAppointmentModal, closeEditAppointmentModal } = modalActions;
  const toast = useToast();

  const addCustomer = async (customerData) => {
    if (!user) return;

    try {
      const franchiseeId = customerData.franchisee_id || getFranchiseeIdForCRUD(user);
      const result = await customerService.createCustomer(franchiseeId, customerData);
      closeAddCustomerModal();
      toast.success('Customer created successfully');
      await refreshData();

      // Navigate to the new customer's details page
      const newCustomer = result?.customer || result;
      if (newCustomer?.id) {
        navigation.handleNavigation(VIEWS.CUSTOMER_DETAILS, newCustomer);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      const details = error.response?.data?.details;
      if (details?.length) {
        toast.error(details.map(d => d.msg).join(', '));
      } else {
        toast.error(error.response?.data?.error || 'Failed to create customer');
      }
    }
  };

  const updateCustomer = async (customerData) => {
    if (!user || !customerData.id) return;

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customerData.franchisee_id);
      const result = await customerService.updateCustomer(franchiseeId, customerData.id, customerData);

      // Update the in-view customer with fresh data from API
      const updatedCustomer = result?.customer || result;
      if (updatedCustomer?.id) {
        setSelectedCustomer(updatedCustomer);
      }

      toast.success('Customer updated successfully');
      await refreshData();
    } catch (error) {
      console.error('Error updating customer:', error);
      const details = error.response?.data?.details;
      if (details?.length) {
        toast.error(details.map(d => d.msg).join(', '));
      } else {
        toast.error(error.response?.data?.error || 'Failed to update customer');
      }
      throw error;
    }
  };

  const deleteCustomer = async (customerId, customerFranchiseeId) => {
    if (!user) return;

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, customerFranchiseeId);

      // Delete customer jobs first (if any)
      const customerJobs = appData.jobs.filter(job => job.customer_id === customerId);
      for (const job of customerJobs) {
        await jobService.deleteJob(getFranchiseeIdForCRUD(user, job.franchisee_id), job.id);
      }

      // Delete customer
      await customerService.deleteCustomer(franchiseeId, customerId);

      // Clear selection and navigate back if needed
      if (navigation.selectedCustomer && navigation.selectedCustomer.id === customerId) {
        setSelectedCustomer(null);
        setCurrentView(VIEWS.CUSTOMERS);
      }

      toast.success('Customer deleted successfully');
      await refreshData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const addJob = async (jobData) => {
    if (!user) return;

    try {
      // Resolve franchisee from the selected customer so admin doesn't get 'admin-user-id'
      const customer = appData.customers.find(c => c.id === jobData.customer_id);
      const franchiseeId = getFranchiseeIdForCRUD(user, customer?.franchisee_id);
      await jobService.createJob(franchiseeId, {
        ...jobData,
        status: JOB_STATUS.SCHEDULED
      });
      closeAddJobModal();
      toast.success('Job created successfully');
      await refreshData();
    } catch (error) {
      console.error('Error adding job:', error);
      toast.error('Failed to create job');
    }
  };

  const updateJob = async (jobId, jobData) => {
    if (!user) return;

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, jobData.franchisee_id);
      const result = await jobService.updateJob(franchiseeId, jobId, jobData);

      // Update the in-view job with fresh data from API
      const updatedJob = result?.job || result;
      if (updatedJob?.id) {
        setSelectedJob(updatedJob);
      }

      closeAddJobModal();
      toast.success('Job updated successfully');
      await refreshData();
    } catch (error) {
      console.error('Error updating job:', error);
      const details = error.response?.data?.details;
      if (details?.length) {
        toast.error(details.map(d => d.msg).join(', '));
      } else {
        toast.error(error.response?.data?.error || 'Failed to update job');
      }
      throw error;
    }
  };

  const addAppointment = async (appointmentData) => {
    if (!user) return;

    try {
      // Resolve franchisee from the job so admin doesn't get 'admin-user-id'
      // Check local jobs list first, then fall back to the job passed via modal initialData
      const job = appData.jobs.find(j => j.id === appointmentData.job_id)
        || modalActions.appointmentInitialData?.job;
      const franchiseeId = getFranchiseeIdForCRUD(user, job?.franchisee_id);
      await appointmentService.createAppointment(franchiseeId, appointmentData);
      closeAddAppointmentModal();
      if (modalActions.bumpAppointmentVersion) modalActions.bumpAppointmentVersion();
      toast.success('Appointment created successfully');
    } catch (error) {
      console.error('Error adding appointment:', error);
      toast.error('Failed to create appointment');
      throw error;
    }
  };

  const updateAppointment = async (appointmentId, updateData) => {
    if (!user) return;

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, updateData.franchisee_id);
      await appointmentService.updateAppointment(franchiseeId, appointmentId, updateData);
      closeEditAppointmentModal();
      toast.success('Appointment updated successfully');
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
      throw error;
    }
  };

  const deleteAppointment = async (appointmentId, appointmentFranchiseeId) => {
    if (!user) return;

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, appointmentFranchiseeId);
      await appointmentService.deleteAppointment(franchiseeId, appointmentId);
      toast.success('Appointment deleted');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
      throw error;
    }
  };

  return {
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addJob,
    updateJob,
    addAppointment,
    updateAppointment,
    deleteAppointment
  };
};